import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getAdminListApi } from '~/features/admin/api/get-admin-list.api';
import { getAdminApi } from '~/features/admin/api/get-admin.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { Admin, AdminListParams } from '~/features/admin/types/admin.type';

export interface AdminState {
  isLoading: boolean;
  list: Admin[];
  meta: PaginationMeta | null;
  params: AdminListParams | null;
  selectedAdmin: Admin | null;
}

const initialState: AdminState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedAdmin: null,
};

/**
 * 관리자 목록 조회
 */
export const fetchAdminList = createAsyncThunk<
  PaginatedApiResponse<Admin>,
  AdminListParams | undefined
>('admin/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getAdminListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 관리자 상세 조회
 */
export const fetchAdminDetail = createAsyncThunk<Admin, number>(
  'admin/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getAdminApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const AdminSlice = createSlice({
  initialState,
  name: 'admin',
  reducers: {
    setSelectedAdmin: (state, action) => {
      state.selectedAdmin = action.payload;
    },
    resetSelectedAdmin: state => {
      state.selectedAdmin = null;
    },
    resetAdmin: state => {
      state.selectedAdmin = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchAdminList
      .addCase(fetchAdminList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchAdminList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchAdminList.rejected, state => {
        state.isLoading = false;
      })
      // fetchAdminDetail
      .addCase(fetchAdminDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchAdminDetail.fulfilled, (state, action) => {
        state.selectedAdmin = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchAdminDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedAdmin, resetSelectedAdmin, resetAdmin } = AdminSlice.actions;
export default AdminSlice;
